import LoadingIndicator from '../loading/loadingIndicator';
import css from './button.module.less';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import c, { Argument } from 'classnames';
import _ from 'lodash';
import React, { useState, useRef, useEffect, Ref } from 'react';

export type ButtonProps = {
    ref?: Ref<HTMLDivElement>;
    primary?: boolean;
    danger?: boolean;
    light?: boolean;
    big?: boolean;
    inactive?: boolean;
    left?: boolean;
    progress?: number;
    spinner?: boolean;
    icon?: { iconName: any } | React.ReactNode;
    centerIcon?: boolean;
    /** If true, calls `event.preventDefault()` in the onClick handler */
    preventDefault?: boolean;
    action?: () => void | Promise<void>;
    'data-testid'?: string;
    classNames?: Record<string, Argument>;
} & React.HtmlHTMLAttributes<HTMLDivElement>;

const toElement = (x: any, def?: React.ReactNode) => {
    if (x === undefined) return;
    if (typeof x === 'function') x = x();
    if (x === true) x = def;
    else if (x.iconName) x = <FontAwesomeIcon fixedWidth icon={x} />;
    return x;
};

export function Button({
    ref,
    primary,
    danger,
    light,
    big,
    inactive,
    left,
    className,
    classNames = {},
    children,
    progress,
    spinner,
    icon,
    centerIcon,
    preventDefault,
    action,
    'data-testid': testId,
    ...otherProps
}: ButtonProps) {
    const isMounted = useRef(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const _css = _.mergeWith({}, css, classNames, (a, b) => c(a, b));
    _css.item = c(_css.item, className);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const onClick = !action
        ? undefined
        : async (e: React.MouseEvent) => {
              if (preventDefault) {
                  e.preventDefault();
              }

              try {
                  setIsProcessing(true);
                  await action();
              } finally {
                  if (isMounted.current) setIsProcessing(false);
              }
          };

    const _className = c(
        primary ? _css.primary : light ? _css.light : _css.secondary,
        {
            [_css.danger]: danger,
            [_css.big]: big,
            [_css.inactive]: inactive,
            [_css.processing]: isProcessing,
            [_css.left]: left,
        },
        _css.item,
    );
    const _icon =
        toElement(spinner ?? isProcessing, <LoadingIndicator size={'1rem'} className={_css.loading} center={false} noMargin />) ||
        toElement(icon);

    return (
        <div
            ref={ref}
            data-testid={testId}
            data-primary={primary === true ? 'true' : 'false'}
            className={c(_className, {
                [_css.hasIcon]: _icon,
                [_css.hasContent]: children,
                [_css.centerIcon]: centerIcon,
            })}
            onClick={onClick}
            role="button"
            {...otherProps}
        >
            {progress !== undefined && <div className={_css.progress} style={{ width: `${progress * 100}%` }} />}
            {_icon && <div className={_css.icon}>{_icon}</div>}
            {children && <div className={_css.content}>{children}</div>}
        </div>
    );
}
